import { useEffect, useRef, useState } from 'react';
import { type ChannelState, subscribeChannel } from '@going-once/client';
import type { RealtimeMessage } from '@going-once/contracts/channels';
import { runtimeConfig } from './config';

/**
 * Subscribing to an AppSync Events channel, as a React hook.
 *
 * **The protocol is in `packages/client/src/realtime.ts`**, shared with the phone — the
 * HTTP-host auth header, the batched `event` array, the keep-alive watchdog and the one
 * socket per page all live there with a spec. This file is only the React half: subscribe
 * on mount, unsubscribe on unmount, render the state.
 *
 * **The subscription is the fast path, never the source of truth** (D50).
 */
export type RealtimeSubscription = ChannelState;

/**
 * Subscribe to one channel for as long as the component is mounted.
 *
 * `onMessage` is held in a ref, so a caller can pass a fresh closure every render — the
 * usual arrow-function-in-a-dependency-array trap, which here would reconnect the socket
 * on every render and produce a subscription that never receives anything.
 */
export function useChannel(
  channel: string | undefined,
  onMessage: (message: RealtimeMessage) => void,
): RealtimeSubscription {
  const [state, setState] = useState<ChannelState>({ status: 'idle', live: false });
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => {
    if (!channel) return;
    // Locally, a missing endpoint means `npm run web:config` predates the realtime fields.
    return subscribeChannel(runtimeConfig().realtime, channel, (message) => handler.current(message), setState);
  }, [channel]);

  return state;
}
